import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Avatar from '../Avatar';
import SilverBadge from '../SilverBadge';
import OrgAdminBadge from '../recruiter/OrgAdminBadge';
import { notificationsAPI, profileAPI } from '../../services/api';
import { NotificationItem } from '../../types';

interface PortalHeaderProps {
  onMenuClick?: () => void;
}

const PortalHeader: React.FC<PortalHeaderProps> = ({ onMenuClick }) => {
  const { user, logout, isAuthenticated, isCandidate, isRecruiter, updateUser } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef<HTMLDivElement | null>(null);
  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Profile';

  const profilePath = isRecruiter ? '/recruiter/profile' : '/profile';
  const placeholder = isRecruiter ? 'Search candidates...' : 'Search jobs...';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      try {
        const response = await notificationsAPI.getNotifications({ limit: 10 });
        setNotifications(response.notifications || response.items || []);
        setUnreadCount(response.total_unread ?? 0);
      } catch {
        setNotifications([]);
      }
      try {
        const count = await notificationsAPI.getUnreadCount();
        setUnreadCount(count.count ?? 0);
      } catch {
        // ignore — fall back to total_unread from list response
      }
    };

    fetchNotifications();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleToggleAccount = async () => {
    if (!user || busy) return;
    try {
      setBusy(true);
      const response = await profileAPI.updateAccountStatus(!user.is_active);
      updateUser(response.profile);
      if (!response.profile.is_active) {
        logout();
        window.location.href = '/login';
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to update account status');
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || busy) return;
    if (!window.confirm('This will delete your account. Continue?')) return;
    try {
      setBusy(true);
      await profileAPI.deleteAccount();
      logout();
      window.location.href = '/';
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to delete account');
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  const handleNotificationClick = async (id: number) => {
    const target = notifications.find((n) => n.id === id);
    const wasUnread = target && !target.is_read;
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    setNotifOpen(false);
    try {
      await notificationsAPI.markRead(id);
    } catch {
      // ignore failed read updates
    }

    if (target?.type === 'job_delegated_to_you' && target?.related_id) {
      navigate(`/recruiter/jobs/${target.related_id}/edit`);
      return;
    }
    if (target?.type === 'job_delegated_away') {
      navigate('/recruiter/jobs');
      return;
    }
    if (target?.type === 'org_invite_redeemed') {
      navigate('/recruiter/org-members');
      return;
    }
    if (target?.type === 'org_admin_promoted' || target?.type === 'org_admin_demoted') {
      navigate(isRecruiter ? '/recruiter/org-members' : '/recruiter/dashboard');
      return;
    }
    if (target?.type === 'org_partner_allowlisted') {
      navigate('/partner/delegated-jobs');
      return;
    }
    if (target?.type === 'delegated_job_reviewer_added'
        || target?.type === 'delegated_job_submission_received'
        || target?.type === 'delegated_job_existing_user_shared') {
      const jobId = target.related_id;
      navigate(jobId ? `/recruiter/delegated-jobs/${jobId}` : '/recruiter/delegated-jobs');
      return;
    }
    if (target?.type === 'delegated_job_submission_shortlisted') {
      navigate('/partner/delegated-jobs/my-submissions');
      return;
    }

    if (isCandidate) {
      navigate('/applications');
      return;
    }
    if (isRecruiter) {
      navigate('/recruiter/applications');
    }
  };

  const handleSearch = () => {
    const query = searchTerm.trim();
    if (isRecruiter) {
      navigate(query ? `/recruiter/talent?q=${encodeURIComponent(query)}` : '/recruiter/talent');
      return;
    }
    navigate(query ? `/jobs?q=${encodeURIComponent(query)}` : '/jobs');
  };

  return (
    <header className="topbar portal-v2-topbar">
      <div className="topbar__left">
        <button
          type="button"
          className="topbar__menu-btn"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className="portal-v2-header-center">
        <div className="portal-v2-search">
          <span className="portal-v2-search-icon">⌕</span>
          <input
            type="text"
            placeholder={placeholder}
            aria-label={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', position: 'relative' }} ref={menuRef}>
        {isAuthenticated ? (
          <>
            <Link
              to="/help"
              className="topbar__help-icon"
              aria-label="Help Center"
              title="Help Center"
            >
              ?
            </Link>
            <div className="topbar__notif">
              <button
                className="topbar__icon"
                aria-label="Notifications"
                onClick={() => {
                  setNotifOpen((prev) => !prev);
                  setMenuOpen(false);
                }}
              >
                <span className="icon-bell" aria-hidden="true" />
              </button>
              {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
            </div>
            <button
              className="pill"
              style={{ padding: '6px 10px' }}
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              <Avatar
                name={displayName}
                imagePath={user?.profile_image_url || undefined}
                size={36}
              />
              <span className="topbar__user-name">{displayName}</span>
              {user?.is_org_admin && <OrgAdminBadge />}
              <SilverBadge user={user} />
            </button>
            <button
              onClick={handleLogout}
              className="btn ghost"
              style={{ padding: '8px 12px', fontSize: '14px' }}
            >
              Logout
            </button>
            {notifOpen && (
              <div className="dropdown dropdown--notifications">
                <div className="dropdown__title">Notifications</div>
                {notifications.length === 0 ? (
                  <div className="muted" style={{ fontSize: '13px' }}>No notifications yet</div>
                ) : (
                  notifications.map((notif) => (
                    <button
                      key={notif.id}
                      type="button"
                      className="dropdown__link"
                      onClick={() => handleNotificationClick(notif.id)}
                      style={notif.is_read ? { opacity: 0.65 } : undefined}
                    >
                      <div className="notif__title" style={{ fontWeight: notif.is_read ? 400 : 600 }}>
                        {!notif.is_read && (
                          <span
                            aria-hidden="true"
                            style={{
                              display: 'inline-block', width: 8, height: 8,
                              background: 'var(--accent-strong, #2563eb)', borderRadius: '50%', marginRight: 8
                            }}
                          />
                        )}
                        {notif.title}
                      </div>
                      <div className="notif__message muted">{notif.message}</div>
                    </button>
                  ))
                )}
                <Link
                  to="/notifications"
                  className="dropdown__link"
                  onClick={() => setNotifOpen(false)}
                  style={{ textAlign: 'center', fontWeight: 600 }}
                >
                  See all notifications →
                </Link>
              </div>
            )}
            {menuOpen && (
              <div className="dropdown">
                <div className="dropdown__title" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{displayName}</span>
                  {user?.is_org_admin && <OrgAdminBadge />}
                  <SilverBadge user={user} />
                </div>
                <Link to={profilePath} onClick={() => setMenuOpen(false)}>Edit Profile</Link>
                {isCandidate && (
                  <Link to="/settings/notifications" onClick={() => setMenuOpen(false)}>Notifications</Link>
                )}
                <Link to="/change-password" onClick={() => setMenuOpen(false)}>Reset Password</Link>
                <button type="button" className="dropdown__link" onClick={handleToggleAccount} disabled={busy}>
                  {user?.is_active ? 'Pause Account' : 'Resume Account'}
                </button>
                <button type="button" className="dropdown__link danger" onClick={handleDeleteAccount} disabled={busy}>
                  Delete Account
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <Link to="/login" className="pill">Login</Link>
            <Link to="/register" className="btn">Sign Up</Link>
          </>
        )}
      </div>
    </header>
  );
};

export default PortalHeader;
